import moment from "moment";

import ConstanceView from "issara-sdk/apis/ConstanceView_core";

import MonthlyWorkSummaryList from "issara-sdk/apis/MonthlyWorkSummaryList_apps_HRM";
import OTHourDraftCreate from "issara-sdk/apis/OTHourDraftCreate_apps_HRM";
import PayrollRecordList from "issara-sdk/apis/PayrollRecordList_apps_HRM";
import ReCalMonthlyWorkList from "issara-sdk/apis/ReCalMonthlyWorkList_apps_HRM";
import SendPayrollRecordList from "issara-sdk/apis/SendPayrollRecordList_apps_HRM";
import WorkShiftSettingList from "issara-sdk/apis/WorkShiftSettingList_apps_HRM";

import PayrollRecordSerializer from "issara-sdk/types/PayrollRecordSerializer_apps_HRM";

import getPdfMake from "react-lib/appcon/common/pdfMake";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  OptionType,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import FormWorkHoursSummaryReport from "../pdfForm/FormWorkHoursSummaryReport";

import { abbreviateThaiName } from "../HRMInterface";

import { getOrdinal } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  CALCULATE: "CALCULATE",
  CHANGE_DIVISION: "CHANGE_DIVISION",
  PRINT_REPORT: "PRINT_REPORT",
  SAVE_DRAFT: "SAVE_DRAFT",
  SEARCH: "SEARCH",
  SEND_PAYROLL: "SEND_PAYROLL",
} as const;

export const SEQUENCE_NAME = "EmployeeSWH" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeeSWHSequence: Partial<
    MonthlyWorkSummaryType & {
      sequenceIndex: "Action" | "START" | null;
      constance: { HRM_OT_CUTOFF_DATE?: number };
      filter: FilterType;
      monthYearOptions: OptionType[];
    }
  > | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "django" | "errorMessage" | "selectedDivision"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type MonthlyWorkSummaryType = Partial<{
  items: MonthlyWorkSummaryItemType[];
  last_approved: string;
  last_send_payroll: string | null;
  total_employee: number;
  total_employee_approved: number;
  total_sent_payrolls: number;
}>;

type MonthlyWorkSummaryItemType = {
  abbrName: string;
  absent_slot_count?: number;
  carry_actual_hour?: number;
  carry_leave_hour?: number;
  carry_over_deficit?: number;
  carry_over_ot?: number;
  cutoff_over_ot: string;
  cutoff_work_shift_setting_employee_id: number;
  early_slot_count?: number;
  employee_code: string;
  employee_full_name: string;
  employee_id: number;
  isPayroll?: boolean;
  late_slot_count?: number;
  manual_summary?: Record<string, number>;
  ot_1_5hr?: number | string;
  ot_1hr?: number | string;
  ot_3hr?: number | string;
  remaining_actual_hour: number;
  remaining_leave_hour: number;
  remaining_over_deficit: number;
  remaining_over_ot?: string;
  remaining_regular_hour: number;
  remaining_work_shift_setting_employee_id: number;
  sum_ot: number;
  template_summary?: Record<string, number>;
  total_early_minutes?: number;
  total_late_minutes?: number;
  work_shift_setting_employee_id: number;
  created_at: string;
};

type FilterType = Partial<{
  division: number | null;
  workShiftId: number | string | null;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.CHANGE_DIVISION]: { value: number };
  [ACTIONS.SAVE_DRAFT]: { items: MonthlyWorkSummaryItemType[] };
  [ACTIONS.SEND_PAYROLL]: { items: MonthlyWorkSummaryItemType[] };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeSWHSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    { EmployeeSWHSequence: { ...state.EmployeeSWHSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.CALCULATE]: HandleCalculate,
    [ACTIONS.CHANGE_DIVISION]: HandleChangeDivision,
    [ACTIONS.PRINT_REPORT]: HandlePrintReport,
    [ACTIONS.SAVE_DRAFT]: HandleSaveDraft,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SEND_PAYROLL]: HandleSendPayroll,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler<Params<Actions["INIT"]>> = async (controller, params) => {
  const state = controller.getState();

  const divisionId = state.selectedDivision?.id;

  const [options, [constance]] = await Promise.all([
    GetWorkShiftSettingOptions(controller, { division: divisionId }),
    ConstanceView.get({ apiToken: controller.apiToken, params: { list: "HRM_OT_CUTOFF_DATE" } }),
  ]);

  const currentMonth = options.find((option) => option.text === moment().format("MMMM YYYY"));

  const defaultWorkShift = currentMonth?.value || options.slice(-1)[0]?.value || null;

  controller.setState(
    {
      EmployeeSWHSequence: {
        ...state.EmployeeSWHSequence,
        constance: { ...constance?.result },
        filter: { division: state.selectedDivision?.id, workShiftId: defaultWorkShift },
        monthYearOptions: options,
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const result = await GetMonthlyWorkSummaryList(controller);

  state = controller.getState();

  controller.setState({
    EmployeeSWHSequence: { ...state.EmployeeSWHSequence, ...result },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleChangeDivision: Handler<Params<Actions["CHANGE_DIVISION"]>> = async (
  controller,
  params
) => {
  let state = controller.getState();

  const seq = state.EmployeeSWHSequence ?? {};

  controller.setState({
    EmployeeSWHSequence: { ...seq, filter: { ...seq.filter, division: params.value } },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CHANGE_DIVISION]: BLC_STATUS.LOADING },
  });

  const options = await GetWorkShiftSettingOptions(controller, { division: params.value });

  const currentMonth = options.find((option) => option.text === moment().format("MMMM YYYY"));

  const defaultWorkShift = currentMonth?.value || options.slice(-1)[0]?.value || null;

  state = controller.getState();

  controller.setState({
    EmployeeSWHSequence: {
      ...state.EmployeeSWHSequence,
      filter: { ...state.EmployeeSWHSequence?.filter, workShiftId: defaultWorkShift },
      monthYearOptions: options,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CHANGE_DIVISION]: null },
  });
};

const HandleCalculate: Handler<Params<Actions["CALCULATE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CALCULATE]: BLC_STATUS.LOADING },
  });

  const seq = state.EmployeeSWHSequence ?? {};
  const filter = seq.filter ?? {};
  const foundOption = seq.monthYearOptions?.find((option) => option.value === filter.workShiftId);

  const [, error] = await ReCalMonthlyWorkList.get({
    apiToken: controller.apiToken,
    params: {
      ...filter,
      ad_year: foundOption?.original?.ad_year,
      month: foundOption?.original?.month,
    },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  const result = await GetMonthlyWorkSummaryList(controller);

  controller.setState({
    EmployeeSWHSequence: { ...state.EmployeeSWHSequence, ...result },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CALCULATE]: BLC_STATUS.SUCCESS },
  });
};

const HandleSendPayroll: Handler<Params<Actions["SEND_PAYROLL"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEND_PAYROLL]: BLC_STATUS.LOADING },
  });

  const formattedItems = params.items.map((item) => ({
    ...item,
    absent_slot_count: item.absent_slot_count ?? 0,
    carry_leave_hour: item.carry_leave_hour ?? 0,
    carry_over_deficit: item.carry_over_deficit ?? 0,
    carry_over_ot: item.carry_over_ot ?? 0,
    detail: {
      manual_summary: item.manual_summary,
      template_summary: item.template_summary,
    },
    early_slot_count: item.early_slot_count ?? 0,
    late_slot_count: item.late_slot_count ?? 0,
    ot_cut_off: item.cutoff_over_ot,
    remaining_ot: item.remaining_over_ot,
    total_early_minutes: item.total_early_minutes ?? 0,
    total_late_minutes: item.total_late_minutes ?? 0,
    work_shift_setting_employee: item.work_shift_setting_employee_id,
  }));

  const [, error] = await SendPayrollRecordList.create({
    apiToken: controller.apiToken,
    data: { items: formattedItems } as any,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  const result = await GetMonthlyWorkSummaryList(controller);

  controller.setState({
    EmployeeSWHSequence: { ...state.EmployeeSWHSequence, ...result },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEND_PAYROLL]: BLC_STATUS.SUCCESS },
  });
};

const HandleSaveDraft: Handler<Params<Actions["SAVE_DRAFT"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_DRAFT]: BLC_STATUS.LOADING },
  });

  const workShiftId = state.EmployeeSWHSequence?.filter?.workShiftId;

  const formattedItems = params.items.map((item) => ({
    employee: item.employee_id,
    ot_1_5hr: item.ot_1_5hr,
    ot_1hr: item.ot_1hr,
    ot_3hr: item.ot_3hr,
    work_shift_setting: workShiftId,
  }));

  const [, error] = await OTHourDraftCreate.create({
    apiToken: controller.apiToken,
    data: { items: formattedItems } as any,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  const result = await GetMonthlyWorkSummaryList(controller);

  controller.setState({
    EmployeeSWHSequence: { ...state.EmployeeSWHSequence, ...result },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_DRAFT]: BLC_STATUS.SUCCESS },
  });
};

const HandlePrintReport: Handler = async (controller) => {
  const state = controller.getState();

  const seq = state.EmployeeSWHSequence ?? {};
  const items = seq.items ?? [];
  const filter = seq.filter ?? {};
  const foundOption = seq.monthYearOptions?.find((option) => option.value === filter.workShiftId);

  const { ad_year: adYear, month } = foundOption?.original ?? {};
  const monthName = moment(`${adYear}-${month}`).format("MMMM");
  const otCutoffDate = seq.constance?.HRM_OT_CUTOFF_DATE ?? 0;
  const ordinal = getOrdinal(otCutoffDate);

  const docDef = await FormWorkHoursSummaryReport({
    fields: items,
    params: {
      month: monthName,
      ot_cutoff_date: otCutoffDate ? `${ordinal.number}${ordinal.suffix}` : "",
      printed_by: state.django?.user?.full_name || "",
      printed_date: moment().format("DD/MM/YYYY, HH:mm"),
    },
  });

  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  pdf.open();
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetWorkShiftSettingOptions: Handler<{ division: number }, Promise<OptionType[]>> = async (
  controller,
  params
) => {
  const [result] = await WorkShiftSettingList.list({
    apiToken: controller.apiToken,
    params: { division: params.division },
  });

  const items: Record<string, any>[] = result?.items || [];

  const options = items.map((item) => ({
    key: item.id,
    original: item,
    text: moment(`${item.ad_year}-${item.month}`).format("MMMM YYYY"),
    value: item.id,
  }));

  return options.sort((a, b) => {
    // Sort by year first, then by month
    const yearDiff = a.original.ad_year - b.original.ad_year;

    if (yearDiff !== 0) {
      return yearDiff;
    }

    return a.original.month - b.original.month;
  });
};

const GetMonthlyWorkSummaryList: Handler<
  unknown,
  Promise<Partial<MonthlyWorkSummaryType>>
> = async (controller) => {
  const state = controller.getState();

  const seq = state.EmployeeSWHSequence ?? {};
  const filter = seq.filter ?? {};
  const foundOption = seq.monthYearOptions?.find((option) => option.value === filter.workShiftId);

  const urlParams = {
    ...filter,
    ad_year: foundOption?.original?.ad_year,
    month: foundOption?.original?.month,
  };

  const [[summary], [payroll]] = await Promise.all([
    MonthlyWorkSummaryList.get({
      apiToken: controller.apiToken,
      params: urlParams,
    }),
    PayrollRecordList.list({
      apiToken: controller.apiToken,
      params: urlParams,
    }),
  ]);

  const payrollItems: PayrollRecordSerializer[] = payroll?.items || [];
  const formattedItems = payrollItems.map((item) => ({
    ...item,
    ...(item.detail as Record<string, any>),
    abbrName: abbreviateThaiName(item.employee_full_name || ""),
    cutoff_over_ot: item.ot_cut_off,
    isPayroll: true,

    remaining_over_ot: item.remaining_ot,
    sum_ot: Number(item.ot_1hr) + Number(item.ot_1_5hr) + Number(item.ot_3hr),
  }));

  if (summary?.items) {
    const mergedItems: MonthlyWorkSummaryType["items"] = [
      ...(summary?.items || []),
      ...formattedItems,
    ];

    summary.items = mergedItems.map((item) => ({
      ...item,
      ot_1_5hr: item.ot_1_5hr === undefined ? item.ot_1_5hr : String(item.ot_1_5hr),
      ot_1hr: item.ot_1hr === undefined ? item.ot_1hr : String(item.ot_1hr),
      ot_3hr: item.ot_3hr === undefined ? item.ot_3hr : String(item.ot_3hr),
    }));
  }

  const result: Partial<MonthlyWorkSummaryType> = { ...summary };

  return result;
};
